import { newId, type AgendaMutation, type AgendaSyncTable } from '@familyhub/shared';
import type { Table } from 'dexie';
import { stamp } from '../platform/clock';
import { adb, type ActivityRecord, type ChildcareRecord, type PersonRecord } from './db';
import { agendaSync } from './sync';

/**
 * The agenda write actions.
 *
 * Every change takes the same route as groceries: apply it locally so the screen
 * updates at once, queue the mutation, and let the sync runner replay it.
 * Everyone in the household may write any row — there is no handshake action
 * that only works online.
 */

type Draft<T> = Omit<T, 'updated_at' | 'deleted_at' | 'created_at'>;

async function enqueueUpsert<T extends { id: string }>(
  table: AgendaSyncTable,
  dexieTable: Table<T, string>,
  draft: Draft<T>,
): Promise<void> {
  const record = { ...draft, updated_at: stamp(), deleted_at: null } as unknown as T;
  const mutation = { op: 'upsert', table, id: newId(), row: record } as unknown as AgendaMutation;
  await adb.transaction('rw', [dexieTable, adb.outbox], async () => {
    await dexieTable.put(record);
    await adb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void agendaSync.sync();
}

async function enqueueDelete<T extends { id: string; deleted_at: string | null; updated_at: string }>(
  table: AgendaSyncTable,
  dexieTable: Table<T, string>,
  id: string,
): Promise<void> {
  const updated_at = stamp();
  const mutation = { op: 'delete', table, id: newId(), row: { id, updated_at } } as unknown as AgendaMutation;
  await adb.transaction('rw', [dexieTable, adb.outbox], async () => {
    const existing = await dexieTable.get(id);
    if (existing) {
      await dexieTable.put({ ...existing, deleted_at: existing.deleted_at ?? updated_at, updated_at });
    }
    await adb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void agendaSync.sync();
}

/* ---------------- activities ---------------- */

export const saveActivity = (draft: Draft<ActivityRecord>) =>
  enqueueUpsert('agenda_activities', adb.activities, draft);
export const deleteActivity = (id: string) => enqueueDelete('agenda_activities', adb.activities, id);

/* ---------------- childcare ---------------- */

export const saveChildcare = (draft: Draft<ChildcareRecord>) =>
  enqueueUpsert('agenda_childcare', adb.childcare, draft);
export const deleteChildcare = (id: string) => enqueueDelete('agenda_childcare', adb.childcare, id);

/* ---------------- people ---------------- */

export const savePerson = (draft: Draft<PersonRecord>) => enqueueUpsert('agenda_people', adb.people, draft);
export const deletePerson = (id: string) => enqueueDelete('agenda_people', adb.people, id);
